/**
 * EN-4544: on the V2 list and order summary, where an order is with its
 * provider once a Send was pressed: sending, or sent. The API says it
 * (`provider_send`, ordersPg providerSend.ts); legacy orders leave it off.
 * Waiting for Send (EN 4111: a live send happens only on that click) has no
 * badge: nearly every Unfulfilled row waits, so it said nothing and read as
 * "ready" whatever the stock. The shipment card says "Not sent" instead.
 */
import type {
  OrderError,
  OrderProviderSend,
} from '@skupreme/api-contracts/core';
import type { BadgeTone } from '@skupreme/shared/ui';

export type ProviderSendOrder = {
  provider_send?: OrderProviderSend | null;
};

export type ProviderSendInfo = {
  label: string;
  tone: BadgeTone;
  hint: string;
};

const providersText = (providers: readonly string[]) =>
  providers.length ? providers.join(', ') : 'the provider';

/** The badge's words, or null when there is none (waiting for Send, a legacy order, nothing routed, another tab). */
export function getProviderSend(
  order: ProviderSendOrder | null | undefined,
): ProviderSendInfo | null {
  const send = order?.provider_send;
  if (!send) return null;
  const where = providersText(send.providers);
  switch (send.state) {
    case 'sending':
      return {
        label: `Sending to ${where}`,
        tone: 'neutral',
        hint: `Fulfill Shipment was pressed; the order is on its way to ${where}.`,
      };
    case 'sent':
      return {
        label: `Sent to ${where}`,
        tone: 'info',
        hint: `${where} has the order; it has not shipped yet.`,
      };
    default:
      return null;
  }
}

/* The hold reasons that only say a shadow request waits for Send: not an issue to show. */
const WAITING_FOR_SEND_HOLDS: ReadonlySet<string> = new Set([
  'gated',
  'rendered_not_sent',
]);

/** The order's errors without the hold that only says it waits for Send, so the row does not flag a normal wait. */
export function withoutWaitingForSend(
  errors: OrderError[] | undefined,
  order: ProviderSendOrder | null | undefined,
): OrderError[] | undefined {
  if (order?.provider_send?.state !== 'waiting_for_send' || !errors)
    return errors;
  return errors.filter(
    error =>
      !(
        error.process === 'live_send_gated' &&
        WAITING_FOR_SEND_HOLDS.has(error.reason ?? '')
      ),
  );
}
